import type { FlowSeries } from "../../shared/api.js";
import type { Stack } from "../../shared/stack.js";
import { h } from "../dom.js";
import { amount, compact } from "../format.js";

const SVG = "http://www.w3.org/2000/svg";
const W = 720;
const H = 240;
const PAD = { left: 52, right: 14, top: 12, bottom: 26 };
const DAY = 86_400_000;

function s<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number>, text?: string): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  if (text !== undefined) el.textContent = text;
  return el;
}

/** A clean step of at least `raw`. */
function step(raw: number): number {
  if (raw <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(raw));
  return [1, 2, 2.5, 5, 10].map((m) => m * mag).find((v) => v >= raw)!;
}

interface Bucket {
  start: number;
  end: number;
  in: number;
  out: number;
}

/** Every bucket from `from` to `to`, empty ones included; with `days`, hours grouped into local days. */
function fill(series: FlowSeries, days: boolean): Bucket[] {
  const byStart = new Map(series.buckets.map(([at, i, o]) => [at, { in: i, out: o }]));
  const out: Bucket[] = [];
  if (!days) {
    for (let at = series.from; at < series.to; at += series.bucketMs) {
      const b = byStart.get(at);
      out.push({ start: at, end: at + series.bucketMs, in: b?.in ?? 0, out: b?.out ?? 0 });
    }
    return out;
  }
  const dayStart = (t: number) => {
    const d = new Date(t);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };
  for (let at = dayStart(series.from); at < series.to; ) {
    const next = new Date(at);
    next.setDate(next.getDate() + 1);
    out.push({ start: at, end: next.getTime(), in: 0, out: 0 });
    at = next.getTime();
  }
  let i = 0;
  for (const [at, inn, o] of series.buckets) {
    while (i < out.length - 1 && at >= out[i].end) i++;
    out[i].in += inn;
    out[i].out += o;
  }
  return out;
}

function label(b: Bucket, days: boolean, bucketMs: number): string {
  const d = new Date(b.start);
  if (days) return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const t = (x: number) => new Date(x).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const date = bucketMs >= 3_600_000 ? `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} ` : "";
  return `${date}${t(b.start)}–${t(b.end)}`;
}

/** A bar whose data end (the end away from the baseline) is rounded by `r`. */
function bar(x: number, w: number, y0: number, y1: number, r: number): string {
  const up = y1 < y0;
  const len = Math.abs(y1 - y0);
  const rr = Math.min(r, len, w / 2);
  if (up) return `M${x},${y0}V${y1 + rr}Q${x},${y1} ${x + rr},${y1}H${x + w - rr}Q${x + w},${y1} ${x + w},${y1 + rr}V${y0}Z`;
  return `M${x},${y0}V${y1 - rr}Q${x},${y1} ${x + rr},${y1}H${x + w - rr}Q${x + w},${y1} ${x + w},${y1 - rr}V${y0}Z`;
}

/**
 * What came in (up) and went out (down) per bucket, as columns mirrored on
 * one baseline, with a tooltip per bucket.
 */
export function flowChart(series: FlowSeries, stack: Stack, days = false): HTMLElement {
  const wrap = h("div", { class: "chart flow-chart" });
  const buckets = fill(series, days);
  if (!series.buckets.length) {
    wrap.append(h("p", { class: "muted empty" }, "No change recorded in this span."));
    return wrap;
  }
  const maxIn = Math.max(0, ...buckets.map((b) => b.in));
  const maxOut = Math.max(0, ...buckets.map((b) => b.out));
  const tick = step(Math.max(maxIn, maxOut) / 2.5);
  const top = Math.max(tick, Math.ceil(maxIn / tick) * tick);
  const bottom = maxOut > 0 ? Math.ceil(maxOut / tick) * tick : 0;
  const plotH = H - PAD.top - PAD.bottom;
  const y = (v: number) => PAD.top + ((top - v) / (top + bottom)) * plotH;
  const slot = (W - PAD.left - PAD.right) / buckets.length;
  const bw = Math.max(1, Math.min(24, slot - 2));
  const r = bw >= 8 ? 4 : bw >= 4 ? 2 : 0;

  const svg = s("svg", { viewBox: `0 0 ${W} ${H}`, role: "img", "aria-label": `${stack.label ?? stack.name}: in and out per ${days ? "day" : "bucket"}` });
  for (let v = -bottom; v <= top + tick * 0.001; v += tick) {
    svg.append(s("line", { class: v === 0 ? "baseline" : "grid", x1: PAD.left, x2: W - PAD.right, y1: y(v), y2: y(v) }));
    svg.append(s("text", { class: "axis", x: PAD.left - 6, y: y(v) + 4, "text-anchor": "end" }, v === 0 ? "0" : `${v < 0 ? "−" : "+"}${compact(Math.abs(v))}`));
  }
  const labelEvery = Math.max(1, Math.ceil(buckets.length / 6));
  buckets.forEach((b, i) => {
    if (i % labelEvery) return;
    const d = new Date(b.start);
    const text = days || series.bucketMs >= DAY
      ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
      : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    svg.append(s("text", { class: "axis", x: PAD.left + slot * (i + 0.5), y: H - 8, "text-anchor": "middle" }, text));
  });

  const zero = y(0);
  const marks = s("g", {});
  buckets.forEach((b, i) => {
    const x = PAD.left + slot * i + (slot - bw) / 2;
    if (b.in > 0) marks.append(s("path", { class: "bar-in", d: bar(x, bw, zero - 1, Math.min(zero - 2, y(b.in)), r) }));
    if (b.out > 0) marks.append(s("path", { class: "bar-out", d: bar(x, bw, zero + 1, Math.max(zero + 2, y(-b.out)), r) }));
  });
  svg.append(marks);

  const band = s("rect", { class: "band", x: 0, y: PAD.top, width: slot, height: plotH, visibility: "hidden" });
  svg.insertBefore(band, marks);
  const hit = s("rect", { class: "hit", x: PAD.left, y: 0, width: W - PAD.left - PAD.right, height: H });
  svg.append(hit);
  const tip = h("div", { class: "chart-tip", hidden: true });
  const legend = h(
    "div",
    { class: "chart-legend small" },
    h("span", null, h("i", { class: "swatch in" }), "In"),
    h("span", null, h("i", { class: "swatch out" }), "Out"),
  );
  wrap.append(legend, svg, tip);

  hit.addEventListener("pointermove", (ev) => {
    const box = svg.getBoundingClientRect();
    const px = ((ev.clientX - box.left) / box.width) * W;
    const i = Math.min(buckets.length - 1, Math.max(0, Math.floor((px - PAD.left) / slot)));
    const b = buckets[i];
    band.setAttribute("x", String(PAD.left + slot * i));
    band.setAttribute("visibility", "visible");
    const net = b.in - b.out;
    tip.hidden = false;
    tip.replaceChildren(
      h("strong", null, label(b, days, series.bucketMs)),
      h("span", null, h("i", { class: "swatch in" }), `In ${amount(b.in, stack.type)}`),
      h("span", null, h("i", { class: "swatch out" }), `Out ${amount(b.out, stack.type)}`),
      h("span", { class: "muted" }, `Net ${net > 0 ? "+" : net < 0 ? "−" : ""}${amount(Math.abs(net), stack.type)}`),
    );
    const left = ((PAD.left + slot * (i + 0.5)) / W) * box.width;
    tip.style.left = `${Math.max(0, Math.min(left + 12, box.width - 170))}px`;
    tip.style.top = `${(PAD.top / H) * box.height + 24}px`;
  });
  hit.addEventListener("pointerleave", () => {
    band.setAttribute("visibility", "hidden");
    tip.hidden = true;
  });
  return wrap;
}
